/**
 * UnsavedChangesPrompt - "Leave this page? You have unsaved changes."
 *
 * Two things can throw a half-filled form away: leaving the page inside the
 * app (a sidebar link, the back button, any navigate()), and leaving it in
 * the browser (refresh, close the tab, type a URL). While `when` is true this
 * covers both:
 *
 *   - In-app navigation is held by React Router's blocker and a ConfirmModal
 *     asks Stay / Leave, in our own words. Only a change of PATH counts — a
 *     query-string change on the same page (a tab, a filter) is not leaving.
 *   - Browser navigation gets the native "Leave site?" dialog through
 *     beforeunload. Browsers ignore custom text there, so there is none.
 *
 * The blocker needs the data router (RouterProvider — see App.tsx). Outside
 * one — unit tests render pages inside MemoryRouter — the prompt quietly does
 * nothing rather than throwing, so a form can carry it unconditionally.
 *
 * `when` flipping false while a navigation is held releases it without
 * showing the dialog: a successful save clears `when` and navigates in the
 * same tick, and the blocker sees that navigation with the old value.
 */
import React, { useContext, useEffect } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { ConfirmModal } from '../ConfirmModal';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface UnsavedChangesPromptProps {
	/** True while the form has changes that would be lost. */
	when: boolean;
	/** Dialog heading. */
	title?: string;
	/** Dialog body. */
	message?: string;
}

/* ============================================
 * BROWSER HALF — refresh, close, external navigation
 * ============================================ */

function useLeaveWarning(when: boolean): void {
	useEffect(() => {
		if (!when) return undefined;
		const warn = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			/* Chrome still wants returnValue set before it shows the dialog. */
			event.returnValue = '';
		};
		window.addEventListener('beforeunload', warn);
		return () => window.removeEventListener('beforeunload', warn);
	}, [when]);
}

/* ============================================
 * ROUTER HALF — links, back/forward, navigate()
 * ============================================ */

const RouterPrompt: React.FC<Required<UnsavedChangesPromptProps>> = ({ when, title, message }) => {
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) => when && currentLocation.pathname !== nextLocation.pathname,
	);

	/* Released the moment the form is clean again — see the docblock. */
	useEffect(() => {
		if (blocker.state === 'blocked' && !when) blocker.proceed();
	}, [blocker, when]);

	return (
		<ConfirmModal
			isOpen={blocker.state === 'blocked' && when}
			onClose={() => { if (blocker.state === 'blocked') blocker.reset(); }}
			onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed(); }}
			title={title}
			message={message}
			confirmLabel="Leave"
			cancelLabel="Stay"
			variant="danger"
			dataRef="organism-unsaved-changes-prompt"
		/>
	);
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const UnsavedChangesPrompt: React.FC<UnsavedChangesPromptProps> = ({
	when,
	title = 'Leave this page?',
	message = 'You have unsaved changes. If you leave now, they will be lost.',
}) => {
	useLeaveWarning(when);

	/* useBlocker throws outside a data router, so the router half only
	   mounts when there is one to hook into. */
	const dataRouter = useContext(UNSAFE_DataRouterContext);
	if (!dataRouter) return null;

	return <RouterPrompt when={when} title={title} message={message} />;
};

export default UnsavedChangesPrompt;
